import {
  Alert,
  Button,
  Flex,
  Link,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import type { AddonParamData, Error, OfferAddon } from 'BrokerTsLib/src/dtos'
import { Insurer, RiskVariant, SelectionType } from 'BrokerTsLib/src/enums'
import { formatDate, priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import { format } from 'date-fns'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router'

import { CancelContract } from '@/components'
import type {
  CompletedOverviewItemProps
} from '@/components/CompletedOverview/CompletedOverviewItem'
import {
  CompletedOverviewValue,
} from '@/components/CompletedOverview/CompletedOverviewItem'
import { Documents } from '@/components/Documents'
import type { RecapTableRowProps } from '@/components/RecapTable/RecapTableRow'
import { dateFormat } from '@/constants/dateFormat'
import { SendContractContextProvider, SendContractModal } from '@/containers/steps/SendContractModal'
import { useAuth } from '@/context/AuthContext'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSelections } from '@/hooks'
import { useGetInsurance } from '@/hooks/useInsuranceParams'
import {
  useGetAddonProductEnums,
  useGetAddonRiskVariantProducts,
} from '@/hooks/useOfferAddon'
import { useCreateContractAdditionally } from '@/hooks/useWizard'

import { ReactivateContract } from '@/components/ReactivateContract'
import { SendContractButton } from '../../../StepComplete/forms/QuoteInformation'
import { Information } from './Information'

export type ExtraInsuranceInformationProps = {
  offer: OfferAddon
  sectionTitle: string
  sectionNumber: number
}

export function ExtraInsuranceInformation({
  offer,
  sectionTitle,
  sectionNumber,
}: ExtraInsuranceInformationProps) {
  const toast = useToast()
  const { user } = useAuth()
  const { insuranceParamsId, insuranceParams, isContracted, refetchInsuranceParams } = useInsuranceParams()

  const {
    data: addonRiskVariantProducts,
    mutateAsync: getAddonRiskVariantProducts,
  } = useGetAddonRiskVariantProducts()
  const { data: addonProductEnums, mutateAsync: getAddonProductEnums } =
    useGetAddonProductEnums()
  const {
    data: dataInsurance,
    refetch: refetchInsurance,
    isLoading: isLoadingInsurance,
  } = useGetInsurance(offer.offerId)
  const { data: dataFs } = useSelections(SelectionType.FormaSjednani)
  const { mutateAsync: createContract, isPending: isCreatingContract } =
    useCreateContractAdditionally()

  const onContractChange = useCallback(() => {
    refetchInsuranceParams()
    refetchInsurance()
  }, [refetchInsuranceParams, refetchInsurance])

  const [priceChanged, setPriceChanged] = useState(false)
  const [alerts, setAlerts] = useState<Error[]>([])
  const [error, setError] = useState<string | null>(null)

  const insurance = dataInsurance?.data
  const fsEnum = dataFs?.data ?? []
  const cat = insurance?.contractAcceptationType
  const isCanceled = !!insurance?.contractCanceled
  const isRespectAssistance =
    offer?.riskVariantId == RiskVariant.RespectAssistance

  useEffect(() => {
    getAddonRiskVariantProducts({
      offerId: offer?.offerId,
      riskVariantId: offer?.riskVariantId,
    })

    getAddonProductEnums({
      offerId: offer?.offerId,
      riskVariantId: offer?.riskVariantId,
      changedParams: [],
      filterOutput: false,
    })
  }, [])

  const defaultValues = offer.params?.parameterValues.reduce(
    (obj: AddonParamData, c) => {
      return { ...obj, [c.name]: c.value }
    },
    {} as AddonParamData,
  )

  const completedOverviewData: CompletedOverviewItemProps[] = useMemo(() => {
    if (!insurance) return [{ warning: 'Pojištění nebylo sjednáno.' }]

    const items = []

    if (insurance.contractNumber && !isRespectAssistance)
      items.push({
        label: 'Číslo pojistné smlouvy',
        value: insurance.contractNumber,
      })

    if (isRespectAssistance && insurance.variableSymbol)
      items.push({
        label: 'Variabilní symbol',
        value: insurance.variableSymbol,
      })

    if (insurance.contractCreatedDate)
      items.push({
        label: 'Datum sjednání',
        value: format(new Date(insurance.contractCreatedDate), 'd. M. yyyy HH:mm'),
      })

    items.push({
      label: 'Způsob sjednání',
      value: fsEnum.find(
        (x) => x.value === insurance.contractAcceptationType?.toString(),
      )?.description,
    })

    if (insurance.contractNumber)
      items.push({
        label: 'Datum počátku pojištění',
        value:
          offer.insurerId == Insurer.Das
            ? '1 den po připsání první splátky na účet pojistitele'
            : format(new Date(insurance.insuranceStartDate!), 'd. M. yyyy HH:mm'),
      })

    if (cat == 2 && !isCanceled && insurance.paymentDate)
      items.push({
        label: 'Splatnost prvního pojistného',
        value: (
          <Flex sx={{ gap: 4 }}>
            <CompletedOverviewValue>
              {formatDate(new Date(insurance.paymentDate!), dateFormat)}
            </CompletedOverviewValue>
          </Flex>
        ),
        warning: 'Pokud nebude první pojistné uhrazeno, PS nevznikne!<br/>Bližší informace ke splatnosti viz Návrh smlouvy.',
        sx: {
          gridColumn: {
            lg: 'span 2',
          },
        },
      })

    if (isCanceled)
      items.push({
        label: 'Smlouva byla stornována',
        value: formatDate(
          new Date(insurance.contractCanceledDate ?? new Date()),
          dateFormat,
        ) as string,
        isError: true,
      })

    return items
  }, [insurance, cat, fsEnum, isCanceled])

  const additionalInfoData: CompletedOverviewItemProps[] = []

  const infos =
    offer?.infos?.filter(
      (x) => x.message && x.message.indexOf('foto') === -1 && x.value >= 1000,
    ) ?? []
  if (infos.length > 0)
    additionalInfoData.push({
      title: 'Ostatní',
      value: (
        <VStack>
          {infos.map((x) => (
            <Text dangerouslySetInnerHTML={{ __html: x.message }}></Text>
          ))}
        </VStack>
      ),
    })

  const recapItems = useMemo(() => {
    const recapItems: RecapTableRowProps[] = [{
      title: offer.riskVariantName,
      items: [
        {
          label: offer.name,
          value: priceFormat(offer.pricePerYear),
        },
      ],
    }]

    if ((addonProductEnums?.data?.length ?? 0) > 0)
      recapItems.push({
        title: '',
        items: addonProductEnums!.data.map((x) => ({
          label: <>
            <Text fontWeight="normal">{x.paramDescription}</Text>
            <Text mx={2}>-</Text>
            <Text fontWeight="bold">
              {
                x.values.find(
                  (y) =>
                    y.value ===
                    defaultValues?.[
                      x.paramName as keyof AddonParamData
                    ],
                )?.text
              }
            </Text>
          </>
        })),
      })

    return recapItems
  }, [addonRiskVariantProducts, addonProductEnums])

  const handleCreateContractClick = () => {
    createContract({
      paramsId: insuranceParamsId!,
      offerId: offer.offerId,
      acceptChangedPrice: false,
    })
      .then((resp) => {
        if (resp.data.priceChanged) setPriceChanged(true)
        else if (resp.data.errors.length > 0) setAlerts(resp.data.errors)
        else {
          toast({
            title: 'Smlouva byla sjednána.',
            status: 'success',
            duration: 5000,
            isClosable: true,
          })
          refetchInsurance()
        }
      })
      .catch(() => {
        setError('Při sjednání došlo k chybě')
      })
  }

  const hardErrors = offer.errors.filter((x) => !x.softError) ?? []
  const softErrors = offer.errors.filter((x) => x.softError) ?? []

  const sidebarSectionItems: ReactNode[] = []
  if (
    !isLoadingInsurance &&
    insurance?.contractNumber &&
    !isRespectAssistance
  ) {
    //Respect asistence nemá žádnou dokumentaci
    sidebarSectionItems.push(
      <SendContractContextProvider insuranceId={insurance.insuranceId ?? undefined}>
        <SendContractModal />
        <SendContractButton insuranceParams={insuranceParams} insurance={insurance} />
      </SendContractContextProvider>
    )

    if (insurance.cancellationOptions?.canCancel)
      sidebarSectionItems.push(
        <Flex mb={4}>
          <CancelContract
            callback={onContractChange}
            insuranceId={insurance.insuranceId!}
          />
        </Flex>)
    else if (insurance.cancellationOptions?.canReactivate)
      sidebarSectionItems.push(
        <Flex mb={4}>
          <ReactivateContract
            callback={onContractChange}
            insuranceId={insurance.insuranceId!}
          />
        </Flex>,
      )
  }

  if (
    !isLoadingInsurance &&
    !insurance?.contractNumber &&
    hardErrors.length == 0
  ) {
    sidebarSectionItems.push(
      <Button
        type="button"
        colorScheme="green"
        onClick={handleCreateContractClick}
        isLoading={isCreatingContract}
      >
        Sjednat pojištění
      </Button>,
    )
  }

  return (
    <Stack rowGap={12}>
      <Information
        number={sectionNumber}
        sectionTitle={sectionTitle}
        offer={offer}
        completedOverviewData={completedOverviewData}
        completedOverviewProps={{
          sx: {
            gridTemplateColumns: '1fr 1fr',
          },
        }}
        additionalInfoData={additionalInfoData}
        recapProps={{
          heading: 'Pojistné/rok',
          rows: recapItems,
          total: priceFormat(offer.pricePerYear),
          totalProps: { sx: { border: 'none' } },
        }}
        sidebarSectionItems={sidebarSectionItems}
      />

      {!isLoadingInsurance && !insurance?.contractNumber && (
        <Stack
          pl={{
            base: 4,
            lg: 24,
          }}
          pr={{ base: 4, lg: 8 }}
        >
          {(priceChanged || alerts.length > 0 || hardErrors.length > 0) && (
            <Alert colorScheme="red">
              <Stack direction="column">
                {priceChanged && (
                  <Text>
                    Před sjednáním došlo ke změně ceny. Vraťte se na
                    <Link
                      as={RouterLink}
                      colorScheme="blue"
                      to={`/steps/2/${insuranceParamsId!}`}
                    >
                      &nbsp;seznam kalkulací&nbsp;
                    </Link>
                    a vybranou nabídku překalkulujte.
                  </Text>
                )}

                {alerts.length > 0 &&
                  alerts.map((e) => (
                    <Text
                      key={e.message}
                      dangerouslySetInnerHTML={{ __html: e.message ?? '' }}
                    />
                  ))}

                {hardErrors.map((e) => (
                  <Text
                    key={e.message}
                    variant="danger"
                    dangerouslySetInnerHTML={{ __html: e.message ?? '' }}
                  />
                ))}
              </Stack>
            </Alert>
          )}

          {softErrors.length > 0 && (
            <Alert colorScheme="yellow">
              <Stack direction="column">
                {softErrors.map((e) => (
                  <Text key={e.message}>{e.message}</Text>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      )}

      {insurance?.contractNumber && (
        <Documents insuranceParams={insuranceParams!} offer={offer} />
      )}
    </Stack>
  )
}
